import { useId, useState } from 'react';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { cn } from '@/lib/cn';

import IconButton from '../Button/IconButton';
import { DepartmentCombobox } from '../Combobox/DepartmentCombobox';
import type { DepartmentOption } from '../Combobox/DepartmentCombobox/-query/type';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export type Department = {
  id: string;
  name: string;
};

type Props = {
  value?: Department;
  onChange: (option?: Department) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
  excludeIds?: string[];
};

/**
 * Department の選択肢に対応した Select
 */
export function DepartmentSelect(props: Props) {
  const tc = useCommonTranslation();

  const [open, setOpen] = useState(false);
  const popoverId = useId();

  const canClear = Boolean(props.allowEmpty && !props.disabled && props.value);

  const valueToLabelCache = useValueToLabelCache({
    base: props.value ? { [props.value.id]: props.value.name } : undefined,
  });

  const handleValueChange = (
    value: string | null,
    option?: DepartmentOption,
  ) => {
    if (value && option) {
      props.onChange({ id: option.id, name: option.name });
    } else {
      props.onChange(undefined);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={() => setOpen(!open)}>
      <div
        className="relative inline-block"
        style={props.width ? { width: props.width } : undefined}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverId}
            className={cn(
              'relative w-full justify-start overflow-hidden text-ellipsis pr-10',
              !props.value && 'text-neutral-muted-on-fill',
            )}
            disabled={props.disabled}
          >
            <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
              {props.value?.name || props.placeHolder || tc('pleaseSelect')}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon
                iconName="ChevronDownIcon"
                className="shrink-0 opacity-50"
              />
            </div>
          </Button>
        </PopoverTrigger>
        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              aria-label="クリア"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onChange(undefined);
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>
      <PopoverContent
        id={popoverId}
        className="w-full overflow-hidden p-0"
        style={props.width ? { width: props.width } : undefined}
        align="start"
      >
        <DepartmentCombobox
          value={props.value?.id ?? null}
          onValueChange={handleValueChange}
          valueToLabelCache={valueToLabelCache}
          excludeIds={props.excludeIds}
        />
      </PopoverContent>
    </Popover>
  );
}
